import { useState, useEffect } from "react";
import { FilePen } from "lucide-react";
import { api } from "@/lib/api";
import DocRenderer from "@/components/admin/DocBlocks";
import { buildProposalDoc, PLAN_FROM_PRICE } from "@/content/engagementLetter";

const PLANS = Object.keys(PLAN_FROM_PRICE);
const inputCls =
  "w-full rounded-xl border-2 border-navy/15 bg-white px-4 py-3 text-base text-navy focus:border-terracotta focus:outline-none";

export default function ProposalTab() {
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({
    leadId: "",
    clientName: "",
    clientAbn: "",
    planName: "Growth",
    fee: PLAN_FROM_PRICE.Growth,
    startDate: new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }),
  });
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.get("/admin/leads").then(({ data }) => setLeads(data.leads)).catch(() => {});
  }, []);

  const pickLead = (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    setForm({
      ...form,
      leadId,
      clientName: lead ? (lead.business || lead.name || "") : form.clientName,
    });
  };

  const set = (key) => (e) => {
    const next = { ...form, [key]: e.target.value };
    if (key === "planName") next.fee = PLAN_FROM_PRICE[e.target.value];
    setForm(next);
  };

  const generate = (e) => {
    e.preventDefault();
    setDoc(buildProposalDoc(form));
  };

  if (doc) {
    return (
      <div>
        <div className="mb-6 flex flex-wrap gap-2 no-print">
          <button
            data-testid="proposal-edit-button"
            onClick={() => setDoc(null)}
            className="rounded-full bg-white border border-border px-5 py-2 text-sm font-bold text-navy/70 hover:text-navy transition-colors"
          >
            ← Edit details
          </button>
          <button
            data-testid="proposal-print-button"
            onClick={() => window.print()}
            className="rounded-full bg-terracotta px-5 py-2 text-sm font-bold text-white hover:bg-terracotta-hover transition-colors"
          >
            Download as PDF
          </button>
        </div>
        <DocRenderer meta={doc.meta} sections={doc.sections} testId="proposal-doc" />
      </div>
    );
  }

  return (
    <form data-testid="proposal-form" onSubmit={generate} className="max-w-xl">
      <p className="text-sm text-navy/60 leading-relaxed">
        Generate a filled-in engagement letter for a prospect. Review it, then Download as PDF to send.
        The template carries the unregistered-agent wording (wording A) you confirmed.
      </p>

      <label htmlFor="p-lead" className="mt-6 block text-sm font-bold text-navy">Prefill from a lead (optional)</label>
      <select id="p-lead" data-testid="proposal-lead-select" value={form.leadId} onChange={(e) => pickLead(e.target.value)} className={`mt-1.5 ${inputCls}`}>
        <option value="">Select a lead…</option>
        {leads.map((l) => (
          <option key={l.id} value={l.id}>
            {(l.business || l.name || l.email)} ({l.type})
          </option>
        ))}
      </select>

      <label htmlFor="p-client" className="mt-5 block text-sm font-bold text-navy">Client legal name *</label>
      <input id="p-client" data-testid="proposal-client-input" required value={form.clientName} onChange={set("clientName")} placeholder="Citizen Plumbing Pty Ltd" className={`mt-1.5 ${inputCls}`} />

      <label htmlFor="p-abn" className="mt-5 block text-sm font-bold text-navy">Client ABN (optional)</label>
      <input id="p-abn" data-testid="proposal-abn-input" value={form.clientAbn} onChange={set("clientAbn")} placeholder="12 345 678 901" className={`mt-1.5 ${inputCls}`} />

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="p-plan" className="block text-sm font-bold text-navy">Plan</label>
          <select id="p-plan" data-testid="proposal-plan-select" value={form.planName} onChange={set("planName")} className={`mt-1.5 ${inputCls}`}>
            {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="p-fee" className="block text-sm font-bold text-navy">Agreed monthly fee ($, ex GST)</label>
          <input id="p-fee" data-testid="proposal-fee-input" type="number" min="0" value={form.fee} onChange={set("fee")} className={`mt-1.5 ${inputCls}`} />
        </div>
      </div>

      <label htmlFor="p-start" className="mt-5 block text-sm font-bold text-navy">Start date</label>
      <input id="p-start" data-testid="proposal-start-input" value={form.startDate} onChange={set("startDate")} className={`mt-1.5 ${inputCls}`} />

      <button
        type="submit"
        data-testid="proposal-generate-button"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-3.5 text-sm font-bold text-white hover:bg-terracotta-hover transition-colors"
      >
        <FilePen className="w-4 h-4" aria-hidden="true" /> Generate engagement letter
      </button>
    </form>
  );
}
